import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  LogIn, LogOut, Wallet, Bitcoin, Bot, Layers, TrendingUp, TrendingDown,
  Plus, Trash2, ExternalLink, Loader2, Calculator,
} from 'lucide-react';
import Sim360Neon from './Sim360Neon';
import PerformanceCard from './PerformanceCard';
import MTModule from './MTModule';
import AITradingDashboard from './AITradingDashboard';
import ConstructionWelcome from './ConstructionWelcome';
import ConstructionBubble from './ConstructionBubble';
import CoreDailyBrief from './CoreDailyBrief';
import SynergyLab from './SynergyLab';
import BacktestLab from './BacktestLab';
import LiveTradeFeed from './LiveTradeFeed';
import { computeKap3Summary, projectKap3Purchase, KAP3_DAILY_RATE, KAP3_NET_FACTOR } from '../utils/kap3Math';
import { apiFetch } from '../utils/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_SRC = process.env.REACT_APP_LOGO_URL;

/* ─────────────────────────────────────────────────────────────────
   CoreInsightsSection — wrapper that fetches /api/ai-bots/core-stats
   ONCE and feeds the 3 new Core AI widgets (Daily Brief, Synergy Lab,
   Backtest Lab). Renders only when at least one core bot is registered.
   ───────────────────────────────────────────────────────────────── */
const CoreInsightsSection = () => {
  const [bots, setBots] = useState(null);
  useEffect(() => {
    apiFetch(`${API}/ai-bots/core-stats`)
      .then((r) => r.json())
      .then((d) => setBots(d?.bots || []))
      .catch(() => setBots([]));
  }, []);
  if (bots === null || bots.length === 0) {
    // Render only the Daily Brief which has its own empty state, so members
    // see a "syncing" placeholder for transparency.
    return (
      <section className="mt-8 mb-6" data-testid="core-insights-section">
        <div className="mb-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#00f4ff] mb-1">// 3 Core AI Insights</div>
          <h2 className="text-xl font-black text-white">Daily pulse · Live feed · Synergy · Backtest</h2>
        </div>
        <div className="space-y-6">
          <CoreDailyBrief />
          <LiveTradeFeed />
        </div>
      </section>
    );
  }
  return (
    <section className="mt-8 mb-6 space-y-6" data-testid="core-insights-section">
      <div className="mb-1">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#00f4ff] mb-1">// 3 Core AI Insights</div>
        <h2 className="text-xl sm:text-2xl font-black text-white">Daily pulse · Live feed · Synergy · Backtest</h2>
        <p className="text-[#9EB4C0] text-sm mt-1">Bloomberg-style brief, mathematical proof of complementarity, and a &ldquo;what-if&rdquo; time machine.</p>
      </div>
      <CoreDailyBrief />
      <LiveTradeFeed />
      <SynergyLab bots={bots} />
      <BacktestLab bots={bots} />
    </section>
  );
};

const fmtUsd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtUsdShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${(v).toFixed(0)}`;
};

// ─────────────────────── Login Screen ───────────────────────
const FORGOT_URL = 'https://my.thedcaclub.com/tickets';

const LoginScreen = () => {
  const { loginWithEmail, registerWithEmail } = useAuth();
  // Read ?mode=register|login from URL (deeplinks from home promo)
  const initialMode = (() => {
    try {
      const m = new URLSearchParams(window.location.search).get('mode');
      return m === 'register' ? 'register' : 'login';
    } catch { return 'login'; }
  })();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'register') {
        await registerWithEmail(email.trim(), password, name.trim());
      } else {
        await loginWithEmail(email.trim(), password);
      }
      // useAuth.setUser triggers re-render; nothing else needed
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-10"
      style={{
        backgroundColor: '#020817',
        backgroundImage:
          'radial-gradient(900px 500px at 20% 0%, rgba(0,244,255,0.10), transparent 60%), radial-gradient(700px 400px at 100% 100%, rgba(124,58,237,0.12), transparent 60%)',
      }}
      data-testid="login-screen"
    >
      <div
        className="relative w-full max-w-md rounded-2xl p-8 text-white"
        style={{
          background: 'linear-gradient(180deg, rgba(11,28,56,0.65), rgba(4,8,22,0.85))',
          border: '1px solid rgba(0,244,255,0.25)',
          backdropFilter: 'blur(14px)',
          boxShadow: '0 30px 80px -20px rgba(0,244,255,0.35)',
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          {LOGO_SRC && <img src={LOGO_SRC} alt="DCA" className="h-8 w-auto" />}
          <span className="w-px h-6 bg-white/10" />
          <Sim360Neon size="md" />
        </div>
        <div className="text-[11px] tracking-[0.3em] text-[#00f4ff] uppercase mb-2">My Portafolio</div>
        <h1 className="text-2xl font-black leading-tight mb-1">
          {mode === 'register' ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="text-xs text-[#9EB4C0] mb-5">
          Track your MT trading, BEP20 wallet, KAP3 plans, and DCA Bitcoin in one place.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === 'register' && (
            <div>
              <Label className="text-[11px] text-[#9EB4C0]">Name (optional)</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-[#0b1c38] border-[#0b1c38] text-white h-10"
                data-testid="auth-name"
              />
            </div>
          )}
          <div>
            <Label className="text-[11px] text-[#9EB4C0]">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              className="bg-[#0b1c38] border-[#0b1c38] text-white h-10"
              data-testid="auth-email"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#9EB4C0]">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'register' ? 'Minimum 6 characters' : 'Your password'}
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              required
              className="bg-[#0b1c38] border-[#0b1c38] text-white h-10"
              data-testid="auth-password"
            />
          </div>

          {error && (
            <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full h-11 bg-gradient-to-r from-[#0051ff] to-[#00e5ff] text-[#020817] font-bold rounded-full shadow-[0_0_18px_-4px_rgba(0,114,255,0.75)] hover:brightness-110"
            data-testid="auth-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
            {mode === 'register' ? 'Create account' : 'Sign in'}
          </Button>
        </form>

        <div className="flex items-center justify-between mt-4 text-[12px]">
          <button
            type="button"
            onClick={() => { setError(''); setMode(mode === 'register' ? 'login' : 'register'); }}
            className="text-[#00f4ff] hover:underline"
            data-testid="auth-toggle-mode"
          >
            {mode === 'register' ? 'Already have an account? Sign in' : 'No account yet? Sign up'}
          </button>
          {mode === 'login' && (
            <a
              href={FORGOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#a78bfa] hover:underline"
              data-testid="auth-forgot-link"
              title="We don't send emails — open a support ticket and we'll reset your password."
            >
              Forgot password?
            </a>
          )}
        </div>

        <p className="text-[10px] text-[#556475] text-center mt-5 leading-relaxed">
          We do <strong className="text-[#9EB4C0]">not</strong> send any emails. If you forget your password,
          <a href={FORGOT_URL} target="_blank" rel="noopener noreferrer" className="text-[#a78bfa] hover:underline mx-1">open a ticket</a>
          and we&apos;ll reset it for you.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────── Header ───────────────────────
const PortfolioHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  return (
  <header
    className="sticky top-0 z-50 px-4 lg:px-8 py-3 flex items-center justify-between border-b border-[#0b1c38]"
    style={{ background: 'rgba(4,8,22,0.85)', backdropFilter: 'blur(10px)' }}
    data-testid="portfolio-header"
  >
    <div className="flex items-center gap-3">
      <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
        {LOGO_SRC && <img src={LOGO_SRC} alt="DCA" className="h-7 lg:h-8 w-auto" />}
      </a>
      <span className="hidden sm:inline-block w-px h-6 bg-white/10" />
      <Sim360Neon size="md" className="hidden sm:inline-flex" />
      <span className="hidden md:inline-block px-2 py-0.5 text-[10px] tracking-[0.3em] uppercase rounded-full border border-[#00f4ff]/40 text-[#00f4ff]">
        My Portfolio
      </span>
    </div>
    <div className="flex items-center gap-2 sm:gap-3">
      <a
        href="https://my.thedcaclub.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="portfolio-backoffice-btn"
        title="Backoffice externo"
        className="inline-flex items-center rounded-full border border-[#7c3aed] text-[#a78bfa] hover:bg-[rgba(124,58,237,0.10)] transition h-9 px-3"
      >
        <span className="hidden sm:inline text-xs font-bold tracking-[0.2em] uppercase">Backoffice</span>
        <span className="sm:hidden text-xs font-bold tracking-[0.2em] uppercase">BO</span>
      </a>
      {user.role === 'admin' && (
        <Button
          onClick={() => { navigate('/admin'); }}
          variant="outline" size="sm"
          className="border-[#a78bfa]/50 text-[#a78bfa] hover:bg-[rgba(167,139,250,0.10)] rounded-full"
          data-testid="admin-link-btn"
          title="Admin Panel"
        >
          <span className="text-base mr-1">⚙</span>
          <span className="hidden sm:inline text-xs font-bold tracking-[0.2em] uppercase">Admin</span>
        </Button>
      )}
      {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-2 ring-[#00f4ff]/40" />}
      <div className="hidden md:block text-right leading-tight">
        <div className="text-sm font-semibold text-white">{user.name}</div>
        <div className="text-[10px] text-[#9EB4C0]">{user.email}</div>
      </div>
      <Button
        onClick={onLogout}
        variant="outline" size="sm"
        className="border-[#00f4ff]/40 text-[#00f4ff] hover:bg-[#00f4ff]/10 rounded-full"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Logout</span>
      </Button>
    </div>
  </header>
  );
};

// ─────────────────────── SectionCard ───────────────────────
const SectionCard = ({ icon: Icon, title, badge, accent = '#00f4ff', children, testid, action }) => (
  <Card
    className="bg-[#040816] overflow-hidden"
    style={{ border: `1px solid ${accent}33`, boxShadow: `0 10px 40px -20px ${accent}55` }}
    data-testid={testid}
  >
    <div
      className="px-5 py-3 flex items-center justify-between"
      style={{ background: `linear-gradient(90deg, ${accent}1a, transparent 60%)`, borderBottom: `1px solid ${accent}22` }}
    >
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5" style={{ color: accent }} />
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
      <div className="flex items-center gap-2">
        {action}
        {badge && (
          <span
            className="text-[10px] tracking-[0.25em] uppercase px-2 py-1 rounded-full"
            style={{ color: accent, backgroundColor: `${accent}18`, border: `1px solid ${accent}44` }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

const Stat = ({ label, value, sub, color = '#fff' }) => (
  <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(0,244,255,0.03)', border: '1px solid rgba(0,244,255,0.1)' }}>
    <div className="text-[9px] tracking-[0.25em] uppercase text-[#556475]">{label}</div>
    <div className="text-base font-bold mt-0.5" style={{ color, fontFamily: 'ui-monospace, monospace' }}>{value}</div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color }}>{sub}</div>}
  </div>
);

// ─────────────────────── Unified Welcome Banner ───────────────────────
const UnifiedSummary = ({ user, totalValue, dailyKap3, change24h }) => (
  <div
    className="rounded-2xl p-6 mb-6 relative overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, rgba(0,244,255,0.08) 0%, rgba(124,58,237,0.08) 100%), #040816',
      border: '1px solid rgba(0,244,255,0.25)',
      boxShadow: '0 20px 60px -25px rgba(0,244,255,0.4)',
    }}
    data-testid="unified-summary"
  >
    <div
      className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(circle, rgba(0,244,255,0.18), transparent 70%)' }}
    />
    <div className="relative flex items-center justify-between flex-wrap gap-4">
      <div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#00f4ff]">Welcome back</div>
        <h2 className="text-2xl md:text-3xl font-black text-white mt-1">
          Hey {user.name?.split(' ')[0] || 'Member'} 👋
        </h2>
        <p className="text-sm text-[#9EB4C0] mt-1">Your unified portfolio dashboard.</p>
      </div>
      <div className="text-right">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#9EB4C0]">Total Portfolio Value</div>
        <div
          className="text-3xl md:text-4xl font-black"
          style={{
            color: '#00f4ff',
            textShadow: '0 0 18px rgba(0,244,255,0.5)',
            fontFamily: 'ui-monospace, monospace',
          }}
          data-testid="total-portfolio-value"
        >
          {fmtUsd(totalValue)}
        </div>
        <div className="flex items-center justify-end gap-3 mt-1 text-[11px]">
          {dailyKap3 > 0 && (
            <span className="text-[#22c55e] flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              +{fmtUsd(dailyKap3)}/day (KAP3)
            </span>
          )}
          {change24h !== undefined && change24h !== null && (
            <span className={change24h >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}>
              {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}% BTC 24h
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────── DCA Module ───────────────────────
const DCAModule = ({ purchases, setPurchases, btcPrice, refresh }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount_usd: '', price_usd: '' });
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    const amountUsd = parseFloat(form.amount_usd);
    const priceUsd = parseFloat(form.price_usd) || btcPrice;
    if (!amountUsd || !priceUsd) return;
    setLoading(true);
    await apiFetch(`${API}/dca/purchases`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        purchase_id: `dca_${Date.now()}`, user_id: 'self', asset: 'BTC',
        date: form.date, amount_usd: amountUsd,
        amount_asset: amountUsd / priceUsd, price_usd: priceUsd,
      }),
    });
    setForm({ date: form.date, amount_usd: '', price_usd: '' });
    setLoading(false);
    refresh();
  };

  const handleDelete = async (id) => {
    await apiFetch(`${API}/dca/purchases/${id}`, { method: 'DELETE', credentials: 'include' });
    refresh();
  };

  // Aggregates
  const totalInvested = purchases.reduce((s, p) => s + (p.amount_usd || 0), 0);
  const totalBtc = purchases.reduce((s, p) => s + (p.amount_asset || 0), 0);
  const avgPrice = totalBtc > 0 ? totalInvested / totalBtc : 0;
  const currentValue = totalBtc * btcPrice;
  const profit = currentValue - totalInvested;
  const profitPct = totalInvested > 0 ? (profit / totalInvested) * 100 : 0;

  // DCA vs Lump Sum comparison
  // Lump Sum = if you invested ALL totalInvested on the first purchase date at that date's price
  const sorted = [...purchases].sort((a, b) => new Date(a.date) - new Date(b.date));
  const firstPrice = sorted[0]?.price_usd || avgPrice;
  const lumpBtc = totalInvested > 0 && firstPrice > 0 ? totalInvested / firstPrice : 0;
  const lumpValue = lumpBtc * btcPrice;
  const lumpProfit = lumpValue - totalInvested;
  const dcaWins = profit >= lumpProfit;

  return (
    <SectionCard icon={Bitcoin} title="DCA Bitcoin" accent="#f7931a" badge="Live" testid="dca-module">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Invested" value={fmtUsd(totalInvested)} />
        <Stat label="BTC owned" value={`${totalBtc.toFixed(6)}`} sub={`@ ${fmtUsd(avgPrice)} avg`} />
        <Stat label="Current value" value={fmtUsd(currentValue)} sub={`BTC ${fmtUsd(btcPrice)}`} />
        <Stat
          label="P/L"
          value={`${profit >= 0 ? '+' : ''}${fmtUsd(profit)}`}
          sub={`${profitPct >= 0 ? '+' : ''}${profitPct.toFixed(1)}%`}
          color={profit >= 0 ? '#22c55e' : '#ef4444'}
        />
      </div>

      {/* DCA vs Lump Sum mini-comparison */}
      {purchases.length >= 2 && (
        <div
          className="rounded-lg p-3 mb-4"
          style={{ background: 'rgba(247,147,26,0.05)', border: '1px solid rgba(247,147,26,0.2)' }}
          data-testid="dca-vs-lump"
        >
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-2">
            DCA vs Lump Sum
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[#f7931a] font-bold mb-1">Your DCA</div>
              <div className="text-white">{fmtUsd(currentValue)}</div>
              <div className={`text-[10px] ${profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                {profit >= 0 ? '+' : ''}{fmtUsd(profit)} ({profitPct.toFixed(1)}%)
              </div>
            </div>
            <div>
              <div className="text-[#7c3aed] font-bold mb-1">Lump Sum (day 1)</div>
              <div className="text-white">{fmtUsd(lumpValue)}</div>
              <div className={`text-[10px] ${lumpProfit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                {lumpProfit >= 0 ? '+' : ''}{fmtUsd(lumpProfit)} ({totalInvested > 0 ? (lumpProfit / totalInvested * 100).toFixed(1) : '0'}%)
              </div>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-center" style={{ color: dcaWins ? '#22c55e' : '#9EB4C0' }}>
            {dcaWins ? '🏆 DCA beats Lump Sum by' : '🪙 Lump Sum would have won by'} {' '}
            <span className="font-bold">{fmtUsd(Math.abs(profit - lumpProfit))}</span>
          </div>
        </div>
      )}

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <div>
          <Label className="text-[10px] text-[#9EB4C0]">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="bg-[#0b1c38] border-[#0b1c38] text-white h-9" data-testid="dca-form-date" />
        </div>
        <div>
          <Label className="text-[10px] text-[#9EB4C0]">Amount USD</Label>
          <Input type="number" placeholder="100" value={form.amount_usd}
            onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
            className="bg-[#0b1c38] border-[#0b1c38] text-white h-9" data-testid="dca-form-amount" />
        </div>
        <div>
          <Label className="text-[10px] text-[#9EB4C0]">BTC price (auto)</Label>
          <Input type="number" placeholder={btcPrice ? btcPrice.toFixed(2) : 'auto'} value={form.price_usd}
            onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
            className="bg-[#0b1c38] border-[#0b1c38] text-white h-9" data-testid="dca-form-price" />
        </div>
        <div className="flex items-end">
          <Button onClick={handleAdd} disabled={loading}
            className="w-full h-9 bg-[#f7931a] hover:bg-[#e88514] text-black font-semibold" data-testid="dca-add-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>
      </div>

      <div className="max-h-48 overflow-y-auto space-y-1.5">
        {purchases.length === 0 && (
          <div className="text-xs text-[#556475] text-center py-6 italic">No purchases yet.</div>
        )}
        {purchases.map((p) => (
          <div key={p.purchase_id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg"
            style={{ background: 'rgba(247,147,26,0.06)', border: '1px solid rgba(247,147,26,0.15)' }}>
            <div className="flex-1">
              <span className="text-[#f7931a] font-semibold">{p.date}</span>
              <span className="ml-3 text-white">{fmtUsd(p.amount_usd)}</span>
              <span className="ml-3 text-[#9EB4C0]">@ {fmtUsd(p.price_usd)}</span>
              <span className="ml-3 text-[#9EB4C0]">→ {(p.amount_asset || 0).toFixed(6)} BTC</span>
            </div>
            <button onClick={() => handleDelete(p.purchase_id)} className="text-[#ef4444] hover:text-red-300">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

// ─────────────────────── Wallet Module ───────────────────────
const WalletModule = ({ wallets, balances, refresh }) => {
  const [newAddr, setNewAddr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!newAddr.startsWith('0x') || newAddr.length !== 42) return;
    setLoading(true);
    await apiFetch(`${API}/wallets`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wallet_id: `w_${Date.now()}`, user_id: 'self', address: newAddr, chain: 'bsc' }),
    });
    setNewAddr('');
    setLoading(false);
    refresh();
  };

  const handleDelete = async (id) => {
    await apiFetch(`${API}/wallets/${id}`, { method: 'DELETE', credentials: 'include' });
    refresh();
  };

  return (
    <SectionCard icon={Wallet} title="BEP20 Wallets" accent="#12b7ff" badge="On-chain" testid="wallet-module">
      <div className="flex gap-2 mb-4">
        <Input placeholder="0x... your BEP20 wallet address" value={newAddr}
          onChange={(e) => setNewAddr(e.target.value)}
          className="bg-[#0b1c38] border-[#0b1c38] text-white text-sm" data-testid="wallet-input" />
        <Button onClick={handleAdd} disabled={loading}
          className="bg-[#12b7ff] hover:bg-[#0677aa] text-black font-semibold" data-testid="wallet-add-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
      {wallets.length === 0 && (
        <div className="text-xs text-[#556475] text-center py-6 italic">
          No wallet connected. Paste your BSC (BEP20) address.
        </div>
      )}
      <div className="space-y-2">
        {wallets.map((w) => {
          const b = balances[w.address];
          return (
            <div key={w.wallet_id} className="rounded-lg px-3 py-2.5"
              style={{ background: 'rgba(18,183,255,0.06)', border: '1px solid rgba(18,183,255,0.2)' }}>
              <div className="flex items-center justify-between">
                <a href={`https://bscscan.com/address/${w.address}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-mono text-[#12b7ff] hover:underline flex items-center gap-1">
                  {w.address.slice(0, 8)}…{w.address.slice(-6)}
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button onClick={() => handleDelete(w.wallet_id)} className="text-[#ef4444] hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {b ? (
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <span className="text-[#9EB4C0]">USDT: <span className="text-white font-semibold">{(b.usdt || 0).toFixed(2)}</span></span>
                  <span className="text-[#9EB4C0]">BNB: <span className="text-white font-semibold">{(b.bnb || 0).toFixed(6)}</span></span>
                </div>
              ) : (
                <div className="text-[10px] text-[#556475] mt-1">Loading balance…</div>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ─────────────────────── KAP3 Module (v2 with per-plan tracking) ───────────────────────
const KAP3Module = ({ wallets, activity, summary }) => {
  const [calcAmount, setCalcAmount] = useState(1000);
  const projection = projectKap3Purchase(calcAmount);
  const expanded = summary.plans.slice(0, 6); // show top 6 plans

  return (
    <SectionCard icon={Layers} title="KAP3 Buy Plans" accent="#7c3aed" badge="On-chain" testid="kap3-module">
      {wallets.length === 0 ? (
        <div className="text-xs text-[#556475] text-center py-4 italic mb-3">
          Add a BEP20 wallet to start tracking your KAP3 plans automatically.
        </div>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Stat label="Invested" value={fmtUsd(summary.totalInvested)} sub={`Target: ${fmtUsd(summary.totalTarget)}`} />
            <Stat label="Withdrawn" value={fmtUsd(summary.totalWithdrawn)} sub={`${summary.progressPct.toFixed(1)}% of 2×`} color="#22c55e" />
            <Stat
              label="Pending rewards"
              value={fmtUsd(summary.pendingNet)}
              sub={`+${fmtUsd(summary.dailyRewardNet)}/day net`}
              color="#7c3aed"
            />
            <Stat
              label="Days to full 2×"
              value={isFinite(summary.daysUntilFull2x) ? Math.ceil(summary.daysUntilFull2x).toString() : '∞'}
              sub={isFinite(summary.daysUntilFull2x) ? `≈ ${(summary.daysUntilFull2x / 30).toFixed(1)} months` : ''}
            />
          </div>

          {/* Global 2x progress */}
          <div className="mb-4">
            <div className="flex justify-between text-[10px] text-[#9EB4C0] mb-1">
              <span>Progress to 2× across all plans</span>
              <span>{summary.progressPct.toFixed(1)}%</span>
            </div>
            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${Math.min(100, summary.progressPct)}%`,
                  background: 'linear-gradient(90deg, #7c3aed 0%, #00f4ff 100%)',
                  boxShadow: '0 0 12px rgba(124,58,237,0.6)',
                }}
                data-testid="kap3-progress-bar"
              />
            </div>
          </div>

          {/* Per-plan cards (top N most recent) */}
          {expanded.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0] mb-1">
                Active plans ({summary.plans.length})
              </div>
              {expanded.map((plan, idx) => {
                const a = plan.analytics;
                return (
                  <div key={plan.hash || idx}
                    className="rounded-lg p-3"
                    style={{
                      background: a.isExpired ? 'rgba(34,197,94,0.06)' : 'rgba(124,58,237,0.06)',
                      border: `1px solid ${a.isExpired ? 'rgba(34,197,94,0.2)' : 'rgba(124,58,237,0.2)'}`,
                    }}
                    data-testid={`kap3-plan-${idx}`}
                  >
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{fmtUsd(a.amount)}</span>
                        <span className="text-[#9EB4C0]">{plan.date.slice(0, 10)}</span>
                        {a.isExpired && (
                          <span className="text-[9px] text-[#22c55e] uppercase tracking-wider">✓ Reached 2×</span>
                        )}
                      </div>
                      <span className="text-[#7c3aed] font-mono text-[11px]">
                        Target {fmtUsd(a.target)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-1.5">
                      <div className="h-full rounded-full"
                        style={{
                          width: `${a.progressPct}%`,
                          background: a.isExpired
                            ? 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)'
                            : 'linear-gradient(90deg, #7c3aed 0%, #c084fc 100%)',
                          boxShadow: a.isExpired ? '0 0 8px #22c55e' : '0 0 8px #7c3aed',
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-[#9EB4C0]">
                      <span>+{fmtUsd(a.dailyRewardNet)}/day</span>
                      <span>{a.elapsedDays.toFixed(0)} days elapsed</span>
                      <span>{isFinite(a.daysRemaining) ? `${Math.ceil(a.daysRemaining)} days left` : '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-[10px] text-[#556475] mt-3 text-center">
            Reading on-chain from{' '}
            <a href="https://bscscan.com/address/0x661b713da0e63f729f8895c0d8f7200287969042"
              target="_blank" rel="noopener noreferrer"
              className="text-[#7c3aed] hover:underline">
              KAP3 contract
            </a>
          </div>
        </>
      )}

      {/* Calculator always visible (works without a wallet) */}
      <div
        className="rounded-lg p-3 mt-3"
        style={{ background: 'rgba(0,244,255,0.05)', border: '1px solid rgba(0,244,255,0.2)' }}
        data-testid="kap3-calculator"
      >
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-4 h-4 text-[#00f4ff]" />
          <span className="text-[11px] tracking-[0.25em] uppercase text-[#00f4ff]">
            What if I buy $X today?
          </span>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <Input
            type="number" min="25" step="25" value={calcAmount}
            onChange={(e) => setCalcAmount(parseFloat(e.target.value) || 0)}
            className="bg-[#0b1c38] border-[#0b1c38] text-white text-sm h-8 w-28"
            data-testid="kap3-calc-input"
          />
          <span className="text-xs text-[#9EB4C0]">USDT (multiples of $25)</span>
        </div>
        {calcAmount > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]">
            <div>
              <div className="text-[#556475]">Daily net</div>
              <div className="text-[#00f4ff] font-bold">{fmtUsd(projection.dailyNet)}</div>
            </div>
            <div>
              <div className="text-[#556475]">Monthly net</div>
              <div className="text-[#00f4ff] font-bold">{fmtUsd(projection.monthlyNet)}</div>
            </div>
            <div>
              <div className="text-[#556475]">Total target (2×)</div>
              <div className="text-white font-bold">{fmtUsd(projection.target)}</div>
            </div>
            <div>
              <div className="text-[#556475]">Time to 2×</div>
              <div className="text-white font-bold">{projection.monthsToTarget.toFixed(1)} months</div>
            </div>
          </div>
        )}
        <div className="text-[9px] text-[#556475] italic mt-2">
          Assumes 7%/month constant · 8% withdraw fee · no referral acceleration
        </div>
      </div>
    </SectionCard>
  );
};

// ─────────────────────── MT4/5 — now in MTModule.jsx (real MetaApi integration) ───────────────────────

// ─────────────────────── Main ───────────────────────
const MyPortafolio = () => {
  const { user, loading, logout } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [btcPrice, setBtcPrice] = useState(0);
  const [wallets, setWallets] = useState([]);
  const [balances, setBalances] = useState({});
  const [activity, setActivity] = useState({});

  const [mtTotalEquity, setMtTotalEquity] = useState(0);
  const [mtStats, setMtStats] = useState({ equity: 0, profit: 0, accounts: 0, trades: 0, winRate: 0, byBot: { sigmai: 0, sugair: 0, jairvis: 0, custom: 0 } });
  const handleMtTotals = useCallback((eq, stats) => {
    setMtTotalEquity(eq || 0);
    if (stats) setMtStats(stats);
  }, []);

  const loadAll = useCallback(async () => {
    if (!user) return;
    try {
      const [pRes, bRes, wRes] = await Promise.all([
        apiFetch(`${API}/dca/purchases`, { credentials: 'include' }),
        apiFetch(`${API}/market/btc-price`),
        apiFetch(`${API}/wallets`, { credentials: 'include' }),
      ]);
      const pData = await pRes.json();
      const bData = await bRes.json();
      const wData = await wRes.json();
      setPurchases(pData.items || []);
      setBtcPrice(bData.btc_usd || 0);
      const ws = wData.items || [];
      setWallets(ws);
      // Fetch per-wallet balance + KAP3 activity
      ws.forEach(async (w) => {
        const [br, ar] = await Promise.all([
          apiFetch(`${API}/wallet/balance/${w.address}`),
          apiFetch(`${API}/kap3/activity/${w.address}`),
        ]);
        const bd = await br.json();
        const ad = await ar.json();
        setBalances((prev) => ({ ...prev, [w.address]: bd }));
        setActivity((prev) => ({ ...prev, [w.address]: ad }));
      });
    } catch (e) {
      console.error('Load error:', e);
    }
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // KAP3 aggregated summary across all wallets
  const kap3Summary = useMemo(() => {
    const allBuys = [];
    const allWithdraws = [];
    Object.values(activity).forEach((a) => {
      (a?.buy_plans || []).forEach((b) => allBuys.push(b));
      (a?.withdrawals || []).forEach((w) => allWithdraws.push(w));
    });
    return computeKap3Summary(allBuys, allWithdraws);
  }, [activity]);

  // Wallet aggregates
  const walletTotals = useMemo(() => {
    let usdt = 0, bnb = 0;
    Object.values(balances).forEach((b) => {
      usdt += b?.usdt || 0;
      bnb += b?.bnb || 0;
    });
    return { usdt, bnb };
  }, [balances]);

  // DCA aggregates
  const dcaTotals = useMemo(() => {
    const invested = purchases.reduce((s, p) => s + (p.amount_usd || 0), 0);
    const btc = purchases.reduce((s, p) => s + (p.amount_asset || 0), 0);
    const value = btc * btcPrice;
    const roi = invested > 0 ? ((value - invested) / invested) * 100 : 0;
    return { invested, btc, value, roi };
  }, [purchases, btcPrice]);

  // Total portfolio value (sum across all sources)
  const totalValue =
    dcaTotals.value +
    walletTotals.usdt +
    walletTotals.bnb * 600 + // approx BNB price; replace with real if needed
    kap3Summary.pendingNet +
    mtTotalEquity;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#00f4ff]" style={{ backgroundColor: '#020817' }}>
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginScreen />;

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#020817' }}>
      <PortfolioHeader user={user} onLogout={logout} />
      <main className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <UnifiedSummary
          user={user}
          totalValue={totalValue}
          dailyKap3={kap3Summary.dailyRewardNet}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <DCAModule purchases={purchases} setPurchases={setPurchases} btcPrice={btcPrice} refresh={loadAll} />
          <WalletModule wallets={wallets} balances={balances} refresh={loadAll} />
          <KAP3Module wallets={wallets} activity={activity} summary={kap3Summary} />
          <MTModule onTotalEquity={handleMtTotals} />
        </div>

        {/* AI Trading Dashboard — Bot Battle Board + Risk Radar 2030 */}
        <AITradingDashboard details={mtStats.details || []} />

        {/* === 3 CORE AI INSIGHTS — Daily Brief · Synergy Lab · Backtest Lab === */}
        <CoreInsightsSection />

        {/* Performance Card — shareable */}
        <PerformanceCard
          user={user}
          totalValue={totalValue}
          kap3={{
            invested: kap3Summary.totalInvested,
            target: kap3Summary.totalTarget,
            withdrawn: kap3Summary.totalWithdrawn,
            pending: kap3Summary.pendingNet,
            progressPct: kap3Summary.progressPct,
          }}
          dca={{
            invested: dcaTotals.invested,
            btc: dcaTotals.btc,
            value: dcaTotals.value,
            roi: dcaTotals.roi,
          }}
          wallets={walletTotals}
          aiTrading={{
            equity: mtStats.equity,
            profit: mtStats.profit,
            accounts: mtStats.accounts,
            winRate: mtStats.winRate,
          }}
        />

        <p className="text-[10px] text-[#556475] text-center mt-8 tracking-[0.2em] uppercase">
          © The DCA Club · Educational data viewer · Not financial advice
        </p>
      </main>

      {/* Construction-mode communication (first-login modal + persistent bubble) */}
      <ConstructionWelcome />
      <ConstructionBubble />
    </div>
  );
};

export default MyPortafolio;
